/**
 * Team Members Mutations
 *
 * This file contains all mutation creators for managing workspace members.
 * These use legacyRestFetch since the endpoints are not yet in api-contract.
 *
 * @see packages/unowned/client/src/app/core/services/rest-api.service.ts
 */

import type { MutationOptions, QueryClient } from '@tanstack/react-query';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { queryKey as workspaceQueryKey } from '@/query/workspaceQueryCreator';

// ============================================================================
// Types
// ============================================================================

type MemberPrivilege = 'owner' | 'admin' | 'member';

type BaseResponse = {
  status: 'OK' | 'failed';
  message?: string;
};

// ============================================================================
// Shared Utilities
// ============================================================================

function createFormData(data: Record<string, string | number | boolean>) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(data)) {
    fd.append(key, String(value));
  }
  return fd;
}

async function handleResponse<T = BaseResponse>(
  response: Response,
): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new TeamMembersMutationError(
      errorData.message || `Request failed with status ${response.status}`,
    );
  }
  return response.json();
}

export class TeamMembersMutationError extends Error {
  name = 'TeamMembersMutationError';
}

// ============================================================================
// Role Mutations (promote/demote)
// ============================================================================

type SetMemberPrivilegeInput = {
  teamMemberId: number;
  teamId: number;
  privilege: MemberPrivilege;
};

/**
 * Set a member's role/privilege in the workspace.
 * Used for: promoteToAdmin, demoteToMember, promoteToOwner
 *
 * @endpoint POST /set_team_member_privilege
 */
export async function setMemberPrivilege({
  teamMemberId,
  teamId,
  privilege,
}: SetMemberPrivilegeInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('set_team_member_privilege', {
    method: 'POST',
    body: createFormData({
      team_member_id: teamMemberId,
      team_id: teamId,
      privilege,
    }),
  });
  return handleResponse(response);
}

export function setMemberPrivilegeMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, SetMemberPrivilegeInput> {
  return {
    mutationFn: setMemberPrivilege,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Invitation Mutations
// ============================================================================

type ResendInvitationInput = {
  email: string;
  workspaceId: number;
  userId: string;
};

/**
 * Resend a workspace invitation to a pending member.
 *
 * @endpoint POST /resend_invitation_link
 */
export async function resendInvitation({
  email,
  workspaceId,
  userId,
}: ResendInvitationInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('resend_invitation_link', {
    method: 'POST',
    queryParams: { userid: userId },
    body: createFormData({
      email,
      workspace_id: workspaceId,
    }),
  });
  return handleResponse(response);
}

export function resendInvitationMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, ResendInvitationInput> {
  return {
    mutationFn: resendInvitation,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type CancelInvitationInput = {
  email: string;
  workspaceId: number;
  userId: string;
};

/**
 * Cancel/revoke a pending workspace invitation.
 * Uses the same endpoint as deactivate but with different params.
 *
 * @endpoint POST /delete_workspace_user
 */
export async function cancelInvitation({
  email,
  workspaceId,
  userId,
}: CancelInvitationInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('delete_workspace_user', {
    method: 'POST',
    queryParams: { userid: userId },
    body: createFormData({
      email,
      workspace_id: workspaceId,
    }),
  });
  return handleResponse(response);
}

export function cancelInvitationMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, CancelInvitationInput> {
  return {
    mutationFn: cancelInvitation,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Access Mutations (deactivate/reactivate/remove)
// ============================================================================

type DeactivateOrRemoveInput = {
  email: string;
  workspaceId: number;
  /** If true, removes from workspace. If false, deactivates but keeps in workspace. */
  removeFromWorkspace: boolean;
  userId: string;
};

/**
 * Deactivate a member or remove them from workspace.
 * Used for single member revoke access.
 *
 * @endpoint POST /delete_workspace_user
 */
export async function deactivateOrRemoveWorkspaceUser({
  email,
  workspaceId,
  removeFromWorkspace,
  userId,
}: DeactivateOrRemoveInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('delete_workspace_user', {
    method: 'POST',
    queryParams: { userid: userId },
    body: createFormData({
      email,
      workspace_id: workspaceId,
      remove_from_workspace: removeFromWorkspace,
    }),
  });
  return handleResponse(response);
}

export function deactivateOrRemoveWorkspaceUserMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, DeactivateOrRemoveInput> {
  return {
    mutationFn: deactivateOrRemoveWorkspaceUser,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type BulkDeactivateOrRemoveInput = {
  /** Comma-separated list of emails */
  emailList: string;
  workspaceId: number;
  removeFromWorkspace: boolean;
};

/**
 * Bulk deactivate or remove multiple members.
 *
 * @endpoint POST /bulk_deactivate_or_remove_workspace_user
 */
export async function bulkDeactivateOrRemoveWorkspaceUser({
  emailList,
  workspaceId,
  removeFromWorkspace,
}: BulkDeactivateOrRemoveInput): Promise<BaseResponse> {
  const response = await legacyRestFetch(
    'bulk_deactivate_or_remove_workspace_user',
    {
      method: 'POST',
      body: createFormData({
        email_list: emailList,
        workspace_id: workspaceId,
        remove_from_workspace: removeFromWorkspace,
      }),
    },
  );
  return handleResponse(response);
}

export function bulkDeactivateOrRemoveWorkspaceUserMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, BulkDeactivateOrRemoveInput> {
  return {
    mutationFn: bulkDeactivateOrRemoveWorkspaceUser,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type ReactivateInput = {
  email: string;
  workspaceId: number;
  userId: string;
};

/**
 * Reactivate a deactivated member.
 *
 * @endpoint POST /reactivate_workspace_user
 */
export async function reactivateWorkspaceUser({
  email,
  workspaceId,
  userId,
}: ReactivateInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('reactivate_workspace_user', {
    method: 'POST',
    queryParams: { userid: userId },
    body: createFormData({
      email,
      workspace_id: workspaceId,
    }),
  });
  return handleResponse(response);
}

export function reactivateWorkspaceUserMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, ReactivateInput> {
  return {
    mutationFn: reactivateWorkspaceUser,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type BulkReactivateInput = {
  emails: string[];
  workspaceId: number;
  userId: string;
};

/**
 * Bulk reactivate multiple deactivated members.
 * Note: There is no bulk endpoint, so this calls reactivateWorkspaceUser for each email.
 *
 * @endpoint POST /reactivate_workspace_user (called multiple times)
 */
export async function bulkReactivateWorkspaceUsers({
  emails,
  workspaceId,
  userId,
}: BulkReactivateInput): Promise<BaseResponse> {
  // Call reactivate for each email
  await Promise.all(
    emails.map((email) =>
      reactivateWorkspaceUser({ email, workspaceId, userId }),
    ),
  );
  return { status: 'OK' };
}

export function bulkReactivateWorkspaceUsersMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, BulkReactivateInput> {
  return {
    mutationFn: bulkReactivateWorkspaceUsers,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Delete Mutations (permanent deletion)
// ============================================================================

type DeleteMemberInput = {
  /** The member's user ID to delete */
  targetUserId: number;
  workspaceId: number;
  /** The current admin's user ID (for auth) */
  userId: string;
};

/**
 * Permanently delete a member and all their data.
 *
 * @endpoint POST /delete_user
 */
export async function permanentlyDeleteWorkspaceUser({
  targetUserId,
  workspaceId,
  userId,
}: DeleteMemberInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('delete_user', {
    method: 'POST',
    queryParams: { userid: userId },
    body: createFormData({
      user_id: targetUserId,
      workspace_id: workspaceId,
    }),
  });
  return handleResponse(response);
}

export function permanentlyDeleteWorkspaceUserMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, DeleteMemberInput> {
  return {
    mutationFn: permanentlyDeleteWorkspaceUser,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type BulkDeleteInput = {
  /** Comma-separated list of user IDs */
  userIdList: string;
};

/**
 * Bulk permanently delete multiple members.
 *
 * @endpoint POST /bulk_delete_user_from_workspace
 */
export async function bulkDeleteUsersFromWorkspace({
  userIdList,
}: BulkDeleteInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('bulk_delete_user_from_workspace', {
    method: 'POST',
    body: createFormData({
      user_id_list: userIdList,
    }),
  });
  return handleResponse(response);
}

export function bulkDeleteUsersFromWorkspaceMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, BulkDeleteInput> {
  return {
    mutationFn: bulkDeleteUsersFromWorkspace,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Hide Mutations
// ============================================================================

type HideMemberInput = {
  teamMemberId: number;
  teamId: number;
};

/**
 * Hide a member from the workspace member list.
 *
 * @endpoint POST /hide_team_member
 */
export async function hideTeamMember({
  teamMemberId,
  teamId,
}: HideMemberInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('hide_team_member', {
    method: 'POST',
    body: createFormData({
      team_member_id: teamMemberId,
      team_id: teamId,
    }),
  });
  return handleResponse(response);
}

export function hideTeamMemberMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, HideMemberInput> {
  return {
    mutationFn: hideTeamMember,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

/**
 * Unhide a member from the workspace member list.
 *
 * @endpoint POST /unhide_team_member
 */
export async function unhideTeamMember({
  teamMemberId,
  teamId,
}: HideMemberInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('unhide_team_member', {
    method: 'POST',
    body: createFormData({
      team_member_id: teamMemberId,
      team_id: teamId,
    }),
  });
  return handleResponse(response);
}

export function unhideTeamMemberMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, HideMemberInput> {
  return {
    mutationFn: unhideTeamMember,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

type BulkHideInput = {
  /** Comma-separated list of team member IDs */
  teamMemberIdList: string;
  teamId: number;
};

/**
 * Bulk hide multiple members.
 *
 * @endpoint POST /bulk_hide_team_member
 */
export async function bulkHideTeamMembers({
  teamMemberIdList,
  teamId,
}: BulkHideInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('bulk_hide_team_member', {
    method: 'POST',
    body: createFormData({
      team_member_id_list: teamMemberIdList,
      team_id: teamId,
    }),
  });
  return handleResponse(response);
}

export function bulkHideTeamMembersMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, BulkHideInput> {
  return {
    mutationFn: bulkHideTeamMembers,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

/**
 * Bulk unhide multiple members.
 *
 * @endpoint POST /bulk_unhide_team_member
 */
export async function bulkUnhideTeamMembers({
  teamMemberIdList,
  teamId,
}: BulkHideInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('bulk_unhide_team_member', {
    method: 'POST',
    body: createFormData({
      team_member_id_list: teamMemberIdList,
      team_id: teamId,
    }),
  });
  return handleResponse(response);
}

export function bulkUnhideTeamMembersMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, BulkHideInput> {
  return {
    mutationFn: bulkUnhideTeamMembers,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Join Request Mutations
// ============================================================================

type ProcessJoinRequestInput = {
  workspaceId: number;
  /** User IDs of the requesters */
  userIds: number[];
  /** true = approve, false = decline */
  approved: boolean;
};

/**
 * Approve or decline join requests.
 *
 * @endpoint POST /process_join_request
 */
export async function processJoinRequest({
  workspaceId,
  userIds,
  approved,
}: ProcessJoinRequestInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('process_join_request', {
    method: 'POST',
    body: createFormData({
      workspace_id: workspaceId,
      user_id: userIds.join(','),
      approved,
    }),
  });
  return handleResponse(response);
}

export function processJoinRequestMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, ProcessJoinRequestInput> {
  return {
    mutationFn: processJoinRequest,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Trial Mutations
// ============================================================================

type UpdateLicenseStatusInput = {
  workspaceId: number;
  memberId: number;
  /** e.g., 'rejected' for cancel trial */
  licenseStatus: string;
};

/**
 * Update a member's license status.
 * Used for canceling trials (set licenseStatus to 'rejected').
 *
 * @endpoint POST /update_workspace_member_license_status
 */
export async function updateWorkspaceMemberLicenseStatus({
  workspaceId,
  memberId,
  licenseStatus,
}: UpdateLicenseStatusInput): Promise<BaseResponse> {
  const response = await legacyRestFetch(
    'update_workspace_member_license_status',
    {
      method: 'POST',
      body: createFormData({
        workspace_id: workspaceId,
        member_id: memberId,
        license_status: licenseStatus,
      }),
    },
  );
  return handleResponse(response);
}

export function updateWorkspaceMemberLicenseStatusMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, UpdateLicenseStatusInput> {
  return {
    mutationFn: updateWorkspaceMemberLicenseStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

/**
 * Cancel a member's trial.
 * Convenience wrapper around updateWorkspaceMemberLicenseStatus.
 *
 * @endpoint POST /update_workspace_member_license_status
 */
export async function cancelMemberTrial({
  workspaceId,
  memberId,
}: {
  workspaceId: number;
  memberId: number;
}): Promise<BaseResponse> {
  return updateWorkspaceMemberLicenseStatus({
    workspaceId,
    memberId,
    licenseStatus: 'rejected',
  });
}

export function cancelMemberTrialMutation(
  queryClient: QueryClient,
): MutationOptions<
  BaseResponse,
  Error,
  { workspaceId: number; memberId: number }
> {
  return {
    mutationFn: cancelMemberTrial,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Leave Workspace Mutations
// ============================================================================

/**
 * Leave the current workspace.
 * Used by basic workspace members to leave and revert to their own workspace.
 *
 * @endpoint POST /leave_workspace
 */
export async function leaveWorkspace(): Promise<BaseResponse> {
  const response = await legacyRestFetch('leave_workspace', {
    method: 'POST',
  });
  return handleResponse(response);
}

export function leaveWorkspaceMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, void> {
  return {
    mutationFn: leaveWorkspace,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}

// ============================================================================
// Notetaker Access Mutations
// ============================================================================

type SetMemberNotetakerAccessInput = {
  /** The member's user ID */
  memberId: number;
  /** The workspace ID */
  workspaceId: number;
  /** Whether to allow notetaker/OtterPilot for this member */
  allowNotetaker: boolean;
};

/**
 * Set whether a member can use the notetaker/OtterPilot feature.
 *
 * @endpoint POST /update_workspace_member_oa_access
 * @see packages/unowned/client/src/app/core/services/rest-api.service.ts setTeamMemberAllowOA
 */
export async function setMemberNotetakerAccess({
  memberId,
  workspaceId,
  allowNotetaker,
}: SetMemberNotetakerAccessInput): Promise<BaseResponse> {
  const response = await legacyRestFetch('update_workspace_member_oa_access', {
    method: 'POST',
    body: createFormData({
      member_id: memberId,
      ws_id: workspaceId,
      setting_bool: allowNotetaker,
    }),
  });
  return handleResponse(response);
}

export function setMemberNotetakerAccessMutation(
  queryClient: QueryClient,
): MutationOptions<BaseResponse, Error, SetMemberNotetakerAccessInput> {
  return {
    mutationFn: setMemberNotetakerAccess,
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKey });
    },
  };
}
